/**
 * Presents one application-styled confirmation modal without relying on the
 * browser's visually inconsistent confirm dialog.
 */
import { translate } from './i18n'
import { Log } from './logger'
import { closeDialog, openDialog } from './dialog-transition'

type ConfirmationElements = {
  dialog: HTMLDialogElement
  message: HTMLElement
  detail: HTMLElement
  accept: HTMLButtonElement
  cancel: HTMLButtonElement
}

type ConfirmationState = {
  active: boolean
  dialog: HTMLDialogElement | null
}

declare global {
  interface Window {
    __dishBookConfirmationState?: ConfirmationState
  }
}

/**
 * Shares modal state across the independently bundled browser entry points.
 */
function confirmationState(): ConfirmationState {
  window.__dishBookConfirmationState ??= {
    active: false,
    dialog: null,
  }

  return window.__dishBookConfirmationState
}

/**
 * Asks the user to approve one action while preventing stacked confirmations.
 */
export async function requestConfirmation(message: string, detail = ''): Promise<boolean> {
  return Log.info('Request action confirmation', async () => {
    const state = confirmationState()

    if (state.active) {
      return false
    }

    state.active = true

    try {
      return await showConfirmation(message, detail)
    } finally {
      state.active = false
    }
  })
}

/**
 * Opens the shared modal and resolves after an explicit choice or Escape.
 */
function showConfirmation(message: string, detail: string): Promise<boolean> {
  const elements = confirmationElements()
  elements.message.textContent = message
  elements.detail.textContent = detail
  elements.detail.hidden = detail === ''

  return new Promise((resolve) => {
    let settled = false

    /**
     * Resolves the one active choice and releases every temporary handler.
     */
    const finish = async (accepted: boolean): Promise<void> => {
      if (settled) {
        return
      }

      settled = true
      elements.dialog.removeEventListener('cancel', cancel)

      elements.accept.onclick = null
      elements.cancel.onclick = null

      if (elements.dialog.open) {
        await closeDialog(elements.dialog)
      }

      resolve(accepted)
    }
    /**
     * Treats Escape and the Cancel control as the same explicit rejection.
     */
    const cancel = (event: Event): void => {
      event.preventDefault()
      void finish(false)
    }

    elements.accept.onclick = () => {
      void finish(true)
    }
    elements.cancel.onclick = () => {
      void finish(false)
    }
    elements.dialog.addEventListener('cancel', cancel)

    openDialog(elements.dialog)
    elements.cancel.focus()
  })
}

/**
 * Creates the reusable dialog only when the first confirmation is requested.
 */
function confirmationElements(): ConfirmationElements {
  const state = confirmationState()

  if (state.dialog !== null && state.dialog.isConnected) {
    return elementsFrom(state.dialog)
  }

  const dialog = document.createElement('dialog')
  dialog.className = 'app-confirmation'
  dialog.dataset.confirmationDialog = ''

  dialog.setAttribute('aria-labelledby', 'application-confirmation-message')

  const content = document.createElement('div')
  content.className = 'app-confirmation-content'

  const message = document.createElement('p')
  message.id = 'application-confirmation-message'
  message.className = 'app-confirmation-message'

  const detail = document.createElement('p')
  detail.className = 'app-confirmation-detail'
  detail.hidden = true

  const actions = document.createElement('div')
  actions.className = 'app-confirmation-actions'

  const cancel = document.createElement('button')
  cancel.type = 'button'
  cancel.className = 'btn-secondary'

  cancel.textContent = translate('confirm_cancel')

  const accept = document.createElement('button')
  accept.type = 'button'
  accept.className = 'btn-primary'

  accept.textContent = translate('confirm_accept')

  content.append(message, detail)
  actions.append(cancel, accept)

  dialog.append(content, actions)
  document.body.appendChild(dialog)

  state.dialog = dialog

  return { dialog, message, detail, accept, cancel }
}

/**
 * Retrieves the stable controls from an already-created confirmation modal.
 */
function elementsFrom(dialog: HTMLDialogElement): ConfirmationElements {
  const message = dialog.querySelector<HTMLElement>('#application-confirmation-message')
  const detail = dialog.querySelector<HTMLElement>('.app-confirmation-detail')
  const buttons = dialog.querySelectorAll<HTMLButtonElement>('button')

  const cancel = buttons.item(0)
  const accept = buttons.item(1)

  if (message === null || detail === null || cancel === null || accept === null) {
    throw new Error('Application confirmation dialog is incomplete.')
  }

  return { dialog, message, detail, accept, cancel }
}
